import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ISignAccountOpController } from '@ambire-common/interfaces/signAccountOp'
import BottomSheet from '@common/components/BottomSheet'
import DualChoiceWarningModal from '@common/components/DualChoiceWarningModal'
import useSign from '@common/hooks/useSign'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import text from '@common/styles/utils/text'
import { translateControllerMessage } from '@common/utils/translateControllerMessage'
import useSignAccountOpControllerState from '@web/hooks/useSignAccountOpControllerState'
import useSwapAndBridgeControllerState from '@web/hooks/useSwapAndBridgeControllerState'
import useTransferControllerState from '@web/hooks/useTransferControllerState'
import LedgerConnectModal from '@web/modules/hardware-wallet/components/LedgerConnectModal'
import QrSigningFlowScreen from '@web/modules/hardware-wallet/screens/QrSigningFlowScreen'
import SignAccountOpHardwareWalletSigningModal from '@web/modules/sign-account-op/components/SignAccountOpHardwareWalletSigningModal'
import { getUiType } from '@web/utils/uiType'

import getStyles from './styles'

const { isTab } = getUiType()

type Props = Pick<
  ReturnType<typeof useSign>,
  | 'renderedButNotNecessarilyVisibleModal'
  | 'warningModalRef'
  | 'feePayerKeyType'
  | 'signingKeyType'
  | 'slowPaymasterRequest'
  | 'shouldDisplayLedgerConnectModal'
  | 'handleDismissLedgerConnectModal'
  | 'warningToPromptBeforeSign'
  | 'acknowledgeWarning'
  | 'dismissWarning'
  | 'shouldDisplayQrSigningModal'
  | 'handleQrSingingFlowOnContinuePressed'
  | 'handleQrSigningFlowSubmitSignatureResponse'
  | 'handleQrSigningFlowOnClosePressed'
  | 'handleQrSigningFlowOnRejectPressed'
  | 'handleQrSigningFlowOnBackPressed'
  | 'currentRequest'
  | 'signingStep'
> & {
  signAccountOpState: ISignAccountOpController | null
  autoOpen?: 'warnings'
  actionType?: 'swapAndBridge' | 'transfer'
}

const Modals: FC<Props> = ({
  renderedButNotNecessarilyVisibleModal,
  signAccountOpState,
  warningModalRef,
  feePayerKeyType,
  signingKeyType,
  slowPaymasterRequest,
  shouldDisplayLedgerConnectModal,
  handleDismissLedgerConnectModal,
  warningToPromptBeforeSign,
  acknowledgeWarning,
  dismissWarning,
  shouldDisplayQrSigningModal,
  handleQrSingingFlowOnContinuePressed,
  handleQrSigningFlowSubmitSignatureResponse,
  handleQrSigningFlowOnClosePressed,
  handleQrSigningFlowOnRejectPressed,
  handleQrSigningFlowOnBackPressed,
  currentRequest,
  signingStep,
  autoOpen,
  actionType
}) => {
  const { styles } = useTheme(getStyles)
  const { t } = useTranslation()
  const { signAccountOpController: swapAndBridgeSignAccountOp } = useSwapAndBridgeControllerState()
  const {
    state: { signAccountOpController: transferSignAccountOp }
  } = useTransferControllerState()
  const currentSignAccountOp = useSignAccountOpControllerState()

  const transactionProgress = useMemo(() => {
    const totalTransactions = signAccountOpState?.accountOp?.calls?.length || 0
    const signedTransactionsCount = signAccountOpState?.signedTransactionsCount

    if (
      totalTransactions <= 1 ||
      typeof signedTransactionsCount !== 'number' ||
      signedTransactionsCount < 0
    )
      return null

    return {
      current: Math.min(signedTransactionsCount, totalTransactions),
      total: totalTransactions
    }
  }, [signAccountOpState?.accountOp?.calls?.length, signAccountOpState?.signedTransactionsCount])

  if (renderedButNotNecessarilyVisibleModal === 'warnings') {
    return (
      <BottomSheet
        id="warning-modal"
        closeBottomSheet={!slowPaymasterRequest ? dismissWarning : undefined}
        sheetRef={warningModalRef}
        style={styles.warningsModal}
        type={isTab ? 'modal' : 'bottom-sheet'}
        withBackdropBlur={false}
        shouldBeClosableOnDrag={false}
        autoOpen={autoOpen === 'warnings'}
      >
        {warningToPromptBeforeSign && (
          <DualChoiceWarningModal
            title={translateControllerMessage(warningToPromptBeforeSign.title)}
            description={translateControllerMessage(warningToPromptBeforeSign.text) || ''}
            primaryButtonText={t('Proceed')}
            secondaryButtonText={t('Cancel')}
            onPrimaryButtonPress={acknowledgeWarning}
            onSecondaryButtonPress={dismissWarning}
            type={warningToPromptBeforeSign?.type as any}
          />
        )}
        {slowPaymasterRequest && (
          <DualChoiceWarningModal.Wrapper>
            <DualChoiceWarningModal.ContentWrapper>
              <DualChoiceWarningModal.TitleAndIcon
                title={t('Sending transaction is taking longer than expected')}
                style={spacings.mbTy}
              />
              <DualChoiceWarningModal.Text
                style={{ ...text.center, ...spacings.mbLg }}
                text={t('Please wait...')}
                weight="medium"
              />
              <DualChoiceWarningModal.Text
                style={{ ...text.center, fontSize: 14, ...spacings.mb }}
                text={t('(Reason: paymaster is taking longer than expected)')}
              />
            </DualChoiceWarningModal.ContentWrapper>
          </DualChoiceWarningModal.Wrapper>
        )}
      </BottomSheet>
    )
  }

  if (renderedButNotNecessarilyVisibleModal === 'ledger-connect') {
    return (
      <LedgerConnectModal
        isVisible={shouldDisplayLedgerConnectModal}
        handleClose={handleDismissLedgerConnectModal}
        displayOptionToAuthorize={false}
      />
    )
  }

  if (renderedButNotNecessarilyVisibleModal === 'qr-sign' && currentRequest && signingStep) {
    return (
      <QrSigningFlowScreen
        handleClose={handleQrSigningFlowOnClosePressed}
        isVisible={shouldDisplayQrSigningModal}
        onContinue={handleQrSingingFlowOnContinuePressed}
        currentRequest={currentRequest}
        signingStep={signingStep}
        signingRequest={null}
        transactionProgress={transactionProgress}
        submitSignatureResponse={handleQrSigningFlowSubmitSignatureResponse}
        onReject={handleQrSigningFlowOnRejectPressed}
        handleQrSigningFlowOnBackPressed={handleQrSigningFlowOnBackPressed}
      />
    )
  }

  if (renderedButNotNecessarilyVisibleModal === 'hw-sign' && signAccountOpState) {
    return (
      <SignAccountOpHardwareWalletSigningModal
        signingKeyType={signingKeyType as any}
        feePayerKeyType={feePayerKeyType as any}
        isSignAndBroadcastInProgress={(() => {
          if (actionType === 'swapAndBridge') {
            return !!swapAndBridgeSignAccountOp?.isSignAndBroadcastInProgress
          }
          if (actionType === 'transfer') {
            return !!transferSignAccountOp?.isSignAndBroadcastInProgress
          }

          return currentSignAccountOp ? currentSignAccountOp.isSignAndBroadcastInProgress : false
        })()}
        signAccountOpStatusType={signAccountOpState.status?.type}
        shouldSignAuth={signAccountOpState.shouldSignAuth}
        signedTransactionsCount={signAccountOpState.signedTransactionsCount}
        accountOp={signAccountOpState.accountOp}
        actionType={actionType}
      />
    )
  }

  return null
}

export default Modals
